import AbstractGenerator from '../../../misc/generator';
import AbstractObject from '../../../misc/object';

import Ground from '../objects/ground';
import Panel from '../../../objects/panel';
//import { ScrewGroup } from '@scene/objects/screw';
import Barrier from '../objects/barrier';
import Arrow from '../../../objects/arrow';
import { HTMLEditableLabel, HTMLLabel } from '../../../objects/html-label';
import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';

import Scene from '@scene';
import { LabelsPadding } from '@scene/objects/ground';
import { generateScrewGroup } from './helper';
import * as THREE from 'three';
import { ScrewGroup } from '@scene/objects/screw';
import Line from '@scene/objects/line';
import Cladding, { CladdingTypes } from './cladding';

export default class EdgeSlab extends AbstractGenerator {

  constructor(options, dispacher) {
    super(options);

    if (!this.options.ground) throw new Error('Ground options is required');
    if (!this.options.barrier) throw new Error('Barrier options is required');
    if (!this.options.panel) throw new Error('Panel options is required');

    this.dispatchOptionUpdate = dispacher;

    // Update settings for edge slab object
    this.options.barrier.type = Barrier.types.STAIRS;
    this.options.ground.type = Ground.types.BASE;
    this.options.barrier.tilt = 0;
    this.options.panel.position = Panel.positions.VERTICAL_X;
    if (this.options.screw.count.n === 3) {
      this.options.panel.circleCorrection = 90;
    }

    this.panels = new AbstractObject();
  }

  init3DObject() {

    // Calculate ground height
    this.options.ground.height = this.options.barrier.dpot * this.options.barrier.stayCount;

    // Create ground object
    this.ground = new Ground(this.options.ground);
    this.ground.onOptionUpdate(this.dispatchOptionUpdate);
    this.ground.mesh.rotation.y = Math.PI;
    this.ground.mesh.rotation.x = Math.PI / 2;
    this.layer3D.addObject(this.ground);

    const GROUND_TOP = this.ground.position.y + this.ground.size.depth / 2;
    const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;
    const PANELS_POS_Y = GROUND_TOP + PADDING_TOP - 
      (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2);

    // Create barrier object
    // the HGC dimension must be limited at the level of concrete, so add the offset between bottom of the barrier and level of concrete
    this.options.barrier.baseOffset = -(PANELS_POS_Y + this.options.beam.shift.y - GROUND_TOP);
    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer3D.addObject(this.barrier);

    // Create panel for each barrier stay
    this.barrier.staysPositions.forEach((item, index) => {
      const panel = new Panel({
        ...this.options.panel//,
        //screw: this.options.screw
      });
      panel.onOptionUpdate(this.dispatchOptionUpdate);
      panel.mesh.position.set(item.z, item.y, -item.x);
      this.panels.object.addObject(panel);
      if (index === 1) {
        this.middlePanel = panel;
        this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      } else {
        generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      }
    });

    this.panels.mesh.position.set(
      this.ground.position.x + this.ground.size.width / 2 + this.options.panel.depth / 2,
      PANELS_POS_Y,
      this.ground.position.z
    );

    this.layer3D.addObject(this.panels);

    const PANEL_VERTICAL_MIDDLE = this.panels.position.y;
    const BARRIER_BOTTOM = this.barrier.size.height;

    this.barrier.position.set(
      this.panels.position.x + this.barrier.size.depth / 2 + this.options.panel.depth / 2,
      PANEL_VERTICAL_MIDDLE + BARRIER_BOTTOM + this.barrier.size.height / -2,
      this.panels.position.z
    );
    
    if (this.options.beam.shift) {
      this.barrier.mesh.position.z -= this.options.beam.shift.x;
      this.barrier.mesh.position.y += this.options.beam.shift.y;
    }

    // Create screw labels
    const screwLabels = this.screwGroup.getScrewLabels();
    screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
      htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
      htmlElem.position.add(this.panels.mesh.position);
      htmlElem.optimalCameraDist = 3000;
      htmlElem.zoomFactor = 0.5;
    });
    this.layer3D.addObject(screwLabels);

    if (this.options.object.dimensions.display || this.options.object.dimensions.renderForReport) {
      // display on the report regardless of the display setting
      // Create dpot label
      const dpot = this.barrier.createDpotLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
      this.layer3D.addObject(dpot.arrow);
      this.layer3D.htmlLayer.add(dpot.htmlElement);

      // Create hgs label
      const hgs = this.barrier.createHgsLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
      this.layer3D.addMesh(hgs.arrow);
      this.layer3D.htmlLayer.add(hgs.htmlElement);
    }

    if (this.options.object.dimensions.display) {
      // Add Labels
      this.addLabels(); 

      if (this.options.panel.type === Panel.types.BEND) {
        // Create lyh label
        const lyh = this.middlePanel.createLyhLabel(this.options.object.dimensions);
        this.layer3D.addMesh(lyh.object);
        lyh.htmlElement.optimalCameraDist = 3000;
        lyh.htmlElement.zoomFactor = 0.5;
        this.layer3D.htmlLayer.add(lyh.htmlElement);
      }
    }

    // Create cladding between barrier posts
    if (this.options.cladding.type !== CladdingTypes.None) {
      this.claddings = new AbstractObject();
      for (let i = 0; i < this.barrier.staysPositions.length - 1; i++) {
        let cladding = new Cladding({
          type: this.options.type,
          barrier: this.options.barrier,
          beamGeometry: this.barrier.stays[0].beam && this.barrier.stays[0].beam.geometry,
          cladding: this.options.cladding,
          stayHeight: this.barrier.size.stayHeight
        });
        cladding.mesh.position.z += -this.barrier.staysPositions[i].x;
        this.claddings.object.addObject(cladding);
      }

      this.claddings.position.x = this.barrier.position.x;
      this.claddings.position.y = GROUND_TOP;

      this.layer3D.addObject(this.claddings);
    }

  }

  init2DObject() {
    this.layer2D.addObject(this.panels);

    // Create ground object
    this.options.ground.height = this.options.panel.width + 400;

    this.ground = new Ground(this.options.ground);
    this.ground.onOptionUpdate(this.dispatchOptionUpdate);
    //this.ground.mesh.rotation.y = Math.PI;
    this.ground.mesh.rotation.z = Math.PI;
    this.ground.mesh.rotation.x = Math.PI / 2;
    this.layer2D.addObject(this.ground);

    // Create barrier object
    this.options.barrier.stayCount = 1;
    this.options.barrier.disableTop = true;

    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer2D.addObject(this.barrier);

    // Create panel object
    this.options.panel.position = Panel.positions.VERTICAL_X;

    const GROUND_TOP = this.ground.position.y + this.ground.size.depth / 2;
    const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;

    this.panels.position.set(
      this.ground.position.x + this.ground.size.width / 2 + this.options.panel.depth / 2,
      GROUND_TOP + PADDING_TOP
        - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2),
      this.ground.position.z
    );

    // Create panel object
    const panel = new Panel({
      ...this.options.panel,
      // screw: this.options.screw
    });
    panel.onOptionUpdate(this.dispatchOptionUpdate);
    const panelPosition = this.barrier.staysPositions[0];
    panel.mesh.position.set(panelPosition.z, panelPosition.y, -panelPosition.x);
    this.middlePanel = panel; // hold ref for addLabels
    this.panels.object.addObject(panel);
    this.layer2D.htmlLayer.addLayer(panel.htmlLayer);

    const { compressionLine } = panel.getCompressionLine();
    if (compressionLine) {
      compressionLine.mesh.rotation.copy(panel.rotation);
      compressionLine.mesh.position.copy(this.panels.position);
      this.layer2D.addObject(compressionLine);
      let zonesLayer = panel.createZonesLabels();
      if (zonesLayer) {
        zonesLayer.mesh.rotation.copy(panel.rotation);
        zonesLayer.mesh.position.copy(this.panels.position);
        this.layer2D.addLayer(zonesLayer);
      }
    }

    this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
    const screwLabels = this.screwGroup.getScrewLabels();
    screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
      htmlElem.position.x += this.panels.position.z;
      // htmlElem.position.y += this.panels.position.x;
      htmlElem.position.z += -this.panels.position.y - (this.options.panel.type === Panel.types.BEND ? 
        this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
    });
    this.layer2D.addObject(screwLabels);

    // Update barrier position
    const PANEL_VERTICAL_MIDDLE = this.panels.position.y;
    const BARRIER_BOTTOM = this.barrier.size.height;

    this.barrier.position.set(
      this.panels.position.x + this.barrier.size.depth / 2 + this.options.panel.depth / 2,
      PANEL_VERTICAL_MIDDLE + BARRIER_BOTTOM + this.barrier.size.height / -2,
      this.panels.position.z
    );

    if (this.options.beam.shift) {
      this.barrier.mesh.position.z -= this.options.beam.shift.x;
      this.barrier.mesh.position.y += this.options.beam.shift.y;
    }

    // Add Labels
    this.addLabels();  

    if (this.options.panel.margin && this.options.panel.margin.top) {
      this.panels.mesh.position.y += this.options.panel.margin.top;
    }

    // mask on camera
    this.barrier.mesh.ignoreOnCameraReset = true;
    this.barrier.mesh.traverseVisible(function (child) {
      if (child instanceof THREE.Mesh) {
        child.ignoreOnCameraReset = true;
      }
    });
  }

  addLabels() {
    const layer = this.is3DMode ? this.layer3D : this.layer2D;
    let options = null;
    if (this.is3DMode) {
      options = {
        is3DMode: true,
        rotation: new THREE.Euler(Math.PI / 2, 0, 0), //this.layer3D.mesh.rotation,
        showLabels: this.options.object.dimensions.showLabels,
        displayHorizontally: this.options.object.dimensions.displayHorizontally,
        isSafetyBarrier: true
      };
    }

    let panelHTMLLabelsCorrection = 0;
    if (this.options.panel.margin && this.options.panel.margin.top) {
      panelHTMLLabelsCorrection += this.options.panel.margin.top;
    }

    // Create thickness (depth) label for panel
    let panelDepth;
    if (this.is3DMode) {
      if (this.middlePanel.type === Panel.types.BEND) {
        panelDepth = this.middlePanel.create3DDepthLabelForBend(options);
        layer.mesh.add(panelDepth.object);
      } else {
        panelDepth = this.middlePanel.create3DDepthLabel(options);
        panelDepth.object.rotation.z = Math.PI; // move on the other side to prevent overlap with DPP label
        this.middlePanel.mesh.add(panelDepth.object);
        panelDepth.htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
        panelDepth.htmlElement.position.y = this.panels.position.y - panelDepth.htmlElement.position.x + panelHTMLLabelsCorrection;
        panelDepth.htmlElement.position.x = this.panels.position.x;
        panelDepth.htmlElement.position.z *= -1;
      }
      panelDepth.htmlElement.optimalCameraDist = 3000;
      panelDepth.htmlElement.zoomFactor = 0.5;
    } else {
      panelDepth = this.middlePanel.createDepthLabel();
      panelDepth.htmlElement.position.z -= this.ground.position.y + this.ground.size.depth / 2;
      panelDepth.htmlElement.position.z -= this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;
      panelDepth.htmlElement.position.z -= panelHTMLLabelsCorrection;
      panelDepth.htmlElement.position.z += this.options.panel.type === Panel.types.CIRCLE ? 
        this.options.panel.width / 2 : this.options.panel.height / 2;
    }
    layer.htmlLayer.add(panelDepth.htmlElement);

    // Create Dpp label
    if (this.options.panel.dpp.active) {
      const dpp = this.createDppLabel(this.middlePanel, options);
      layer.htmlLayer.add(dpp.htmlElement);
    }

    // Create ground thickness label
    const groundThicknessLabel = this.ground.getThicknessLabel(
      {
        x: this.ground.position.x + this.ground.size.width / 2,
        y: this.ground.position.y - this.ground.size.depth / 2,
        z: this.ground.position.z - this.ground.size.height / 2 - LabelsPadding
      },
      options
    );
    layer.addMesh(groundThicknessLabel.arrow);
    layer.htmlLayer.add(groundThicknessLabel.htmlElement);

    // Create label for Dy
    const { htmlElement, arrow } = this.screwGroup.createDyLabel(null, options);
    if (this.is2DMode) {
      htmlElement.position.set(
        this.panels.position.z - arrow.position.y,
        this.panels.position.x,
        -this.panels.position.y - this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection + 
          (this.options.panel.type === Panel.types.CIRCLE ? arrow.position.z : this.middlePanel.size.height /2)
      );
    } else {
      htmlElement.position.set(
        this.panels.position.x + this.middlePanel.size.depth / 2,
        this.panels.position.y + this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection -
          (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
        this.panels.position.z + arrow.position.y
      );
      htmlElement.optimalCameraDist = 3000;
      htmlElement.zoomFactor = 0.5;
      
      if (!options.displayHorizontally) {
        htmlElement.align = HTMLEditableLabel.types.align.CENTER;
        htmlElement.position.z += 15;
        htmlElement.setDirection(
          htmlElement.position.clone().setY(htmlElement.position.y + this.middlePanel.options.dy.value / 2),
          htmlElement.position.clone().setY(htmlElement.position.y - this.middlePanel.options.dy.value / 2),
          false
        );
      }
    }
    layer.htmlLayer.add(htmlElement);

    // Create lx (width) label
    const { line: widthLine, htmlElement: widthHtmlElement } = this.middlePanel.createWidthLabel(options);
    if (this.is2DMode) {
      widthHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
      widthHtmlElement.position.x = this.panels.position.z;
    } else {
      widthHtmlElement.position.set(
        this.panels.position.x,
        this.panels.position.y - panelHTMLLabelsCorrection + LabelsPadding + 
          (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
        this.panels.position.z
      );
      widthHtmlElement.optimalCameraDist = 3000;
      widthHtmlElement.zoomFactor = 0.5;
      if (!options.displayHorizontally) {
        widthHtmlElement.setDirection(
          widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z + this.middlePanel.size.width / 2),
          widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z - this.middlePanel.size.width / 2),
          false
        );
      }
      widthHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.BOTTOM;
    }
    this.middlePanel.object.addMesh(widthLine);
    layer.htmlLayer.add(widthHtmlElement);

    // For not CIRCLE panel type
    if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
      // Create ly (height) label
      const { line: heightLine, htmlElement: heightHtmlElement } = this.middlePanel.createHeightLabel(options);
      if (this.is2DMode) {
        heightHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
        heightHtmlElement.position.x = this.middlePanel.size.width / 2 + LabelsPadding;
      } else {
        heightHtmlElement.position.set(
          this.panels.position.x,
          this.panels.position.y,
          this.panels.position.z - this.middlePanel.size.width / 2 - LabelsPadding
        );
        heightHtmlElement.optimalCameraDist = 3000;
        heightHtmlElement.zoomFactor = 0.5;
        if (!options.displayHorizontally) {
          heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
          heightHtmlElement.position.z -= 15;
          heightHtmlElement.setDirection(
            heightHtmlElement.position.clone().setY(heightHtmlElement.position.y + this.middlePanel.size.height / 2),
            heightHtmlElement.position.clone().setY(heightHtmlElement.position.y - this.middlePanel.size.height / 2),
            false
          );
        } else {
          heightHtmlElement.align = HTMLEditableLabel.types.align.LEFT;
        }
      }

      this.middlePanel.object.addMesh(heightLine);
      layer.htmlLayer.add(heightHtmlElement);
    } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
      const { object: circleObject, htmlElement: circleHtmlElement } = this.screwGroup.createScrewCircle(options);
      if (this.is2DMode) {
        circleHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
      } else {
        circleHtmlElement.position.set(
          this.panels.position.x + this.middlePanel.size.depth / 2,
          this.panels.position.y - (circleHtmlElement.position.x - 20),
          this.panels.position.z
        );
        circleHtmlElement.optimalCameraDist = 3000;
        circleHtmlElement.zoomFactor = 0.5;
        if (!options.displayHorizontally) {
          circleHtmlElement.setDirection(
            circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z + this.middlePanel.size.width / 2),
            circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z - this.middlePanel.size.width / 2),
            false
          );
        }
        circleHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
      }
      this.middlePanel.object.addMesh(circleObject);
      layer.htmlLayer.add(circleHtmlElement);
    }

    if (this.middlePanel.options.type === Panel.types.SQUARE) {
      if (this.options.screw.count.n > 1) {
        // Create S1 label
        const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
          this.screwGroup, 
          false, 
          LabelsPadding, 
          this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
          options
        );
        if (s1HtmlElement && s1Object) {
          if (this.is2DMode) {
            s1HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          } else {
            s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
            s1HtmlElement.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2,
              this.panels.position.y - s1HtmlElement.position.z,
              this.panels.position.z
            );
            s1HtmlElement.optimalCameraDist = 3000;
            s1HtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              s1HtmlElement.setDirection(
                s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z + this.screwGroup.options.padding.s1 / 2),
                s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z - this.screwGroup.options.padding.s1 / 2),
                false
              );
            }
          }
          this.middlePanel.object.addMesh(s1Object);
          layer.htmlLayer.add(s1HtmlElement);
        }
      }

      if (this.options.screw.count.m > 1 || this.options.screw.formation === ScrewGroup.formation.TRIANGLE) {
        // Create S2 label
        const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
          this.screwGroup, 
          true, 
          LabelsPadding + this.middlePanel.size.width / 2,
          null,
          options
        );
        if (s2HtmlElement && s2Object) {
          this.middlePanel.object.addMesh(s2Object);
          if (this.is2DMode) {
            s2HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          } else {
            s2HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            s2HtmlElement.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2,
              this.panels.position.y -
                (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2) 
                + this.middlePanel.options.dy.value + this.screwGroup.options.padding.s2 / 2 - panelHTMLLabelsCorrection,
              this.panels.position.z + this.middlePanel.size.width / 2 + LabelsPadding
            );
            s2HtmlElement.optimalCameraDist = 3000;
            s2HtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              s2HtmlElement.position.z += 15;
              s2HtmlElement.setDirection(
                s2HtmlElement.position.clone().setY(s2HtmlElement.position.y + this.screwGroup.options.padding.s2 / 2),
                s2HtmlElement.position.clone().setY(s2HtmlElement.position.y - this.screwGroup.options.padding.s2 / 2),
                false
              );
            } else {
              s2HtmlElement.align = HTMLEditableLabel.types.align.RIGHT;
            }
          }
          layer.htmlLayer.add(s2HtmlElement);
        }
      }
    } else if (this.middlePanel.options.type === Panel.types.BEND && Array.isArray(this.screwGroup.screwCoordinates[1])) {
      // Create S1 label
      const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
        this.screwGroup, 
        false, 
        (this.screwGroup.screwCoordinates[1].length > 1 ? LabelsPadding : LabelsPadding /2), 
        this.middlePanel.size.height / 2 - (this.screwGroup.screwCoordinates[1].length > 1 ? this.middlePanel.options.dy.value : 0),
        options
      );
      if (s1HtmlElement && s1Object) {
        this.middlePanel.object.addMesh(s1Object);
        if (this.is2DMode) {
          s1HtmlElement.position.z -= this.panels.position.y;
        } else {
          if (this.screwGroup.screwCoordinates[1].length > 1) {
            s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
            s1HtmlElement.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2,
              this.panels.position.y - s1HtmlElement.position.z,
              this.panels.position.z
            );
          } else {
            s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            s1HtmlElement.position.set(
              this.panels.position.x + s1HtmlElement.position.y,
              this.panels.position.y - s1HtmlElement.position.z + LabelsPadding /2,
              this.panels.position.z
            );
          }
          s1HtmlElement.optimalCameraDist = 3000;
          s1HtmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
            s1HtmlElement.setDirection(
              s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y + this.screwGroup.options.padding.s1 / 2),
              s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y - this.screwGroup.options.padding.s1 / 2),
              false
            );
          }
        }
        layer.htmlLayer.add(s1HtmlElement);
      }
    }
    
    if (this.options.beam.shift) {
      this.createShiftLabels(layer, options);
      // this.barrier.mesh.position.z -= this.options.beam.shift.x;
      // this.barrier.mesh.position.y += this.options.beam.shift.y;
    }
  }

  setOrientation() {
    super.setOrientation();

    if (this.is2DMode) {
      this.layer2D.mesh.rotation.x = Math.PI / -2;
      this.layer2D.mesh.rotation.y = Math.PI / -2;
    } else {
      this.setHTMLOrientation();
    }
  }

  // setHTMLOrientation() {
  //   for (let i = 0; i < this.layer3D.htmlLayer.htmlLayerElements.length; i++){
  //     let elem = this.layer3D.htmlLayer.htmlLayerElements[i];
  //     if (elem.changeOrientation) {
  //       elem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
  //       elem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 4);
  //       elem.position.applyAxisAngle(new THREE.Vector3(1, 0, ), -Math.PI / 2);
  //     }
  //   }
  // }

  get DPP() {
    return this.options.panel.dpp.active ? this.options.panel.dpp.value : 0;
  }

  createDppLabel(parent, options) {
    const arrow = new Arrow({
      size: -this.options.panel.dpp.value,
      position: {
        x: 0,
        y: -this.options.panel.width / 2 - LabelsPadding,
        z: - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2)
      },
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });

    parent.object.addObject(arrow);
    
    if (this.options.panel.type === Panel.types.CIRCLE) {
      const bottomLine = new Line({
        from: {
          x: 0,
          y: this.panels.position.y,
          z: this.panels.position.z - this.options.panel.width / 2
        },
        to: {
          x: 0,
          y: this.panels.position.y - this.options.panel.width / 2 - LabelsPadding,
          z: this.panels.position.z - this.options.panel.width / 2
        },
        disableDepthTest: true
      });
      parent.object.addObject(bottomLine);
    }

    const htmlElement = new HTMLEditableLabel({
      text: ((!options || !options.is3DMode || options.showLabels) ? 'Dpp = ' : '') + 
        `${unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          dpp: {
            value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });

    if (this.is2DMode) {
      htmlElement.position.set(
        this.panels.position.z + this.options.panel.width / 2 + LabelsPadding,
        this.panels.position.x,
        -this.panels.position.y - this.options.panel.dpp.value / 2
          - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2)
      );
    } else {
      htmlElement.position.set(
        this.panels.position.x,
        this.panels.position.y + this.options.panel.dpp.value / 2 + 
         (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2),
        -this.panels.position.z - this.options.panel.width / 2 - LabelsPadding 
          // - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2)
      );
      htmlElement.optimalCameraDist = 3000;
      htmlElement.zoomFactor = 0.5;
      
      if (!options.displayHorizontally) {
        htmlElement.align = HTMLEditableLabel.types.align.CENTER;
        htmlElement.position.z -= 15;
        htmlElement.setDirection(
          htmlElement.position.clone().setY(
            htmlElement.position.y + Math.max(25, this.DPP / 2)),
          htmlElement.position.clone().setY(
            htmlElement.position.y - Math.max(25, this.DPP / 2)),
          false
        );
      }
    }

    return {
      arrow,
      htmlElement
    };
  }

  createShiftLabels(layer, options) {
    const shiftLabels = this.barrier.createShiftLabels(
      this.dispatchOptionUpdate, 
      this.options.beam.shift, 
      new THREE.Vector3(this.panels.position.z, this.panels.position.y, this.panels.position.x),
      options
    );
    if (shiftLabels) {
      if (shiftLabels.marker) {
        shiftLabels.marker.rotation.z = Math.PI / 2;
        shiftLabels.marker.position.set(
          this.panels.position.x + this.middlePanel.size.depth / 2, 
          this.panels.position.y, 
          this.panels.position.z
        );
        layer.addObject(shiftLabels.marker);
      }
      if (shiftLabels.xLabel) {
        shiftLabels.xLabel.object.rotation.y = Math.PI / 2;
        shiftLabels.xLabel.object.position.x += this.middlePanel.size.depth / 2;
        if (options && options.is3DMode) {
          shiftLabels.xLabel.HTMLObject.position.set(
            shiftLabels.xLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
            -shiftLabels.xLabel.HTMLObject.position.z,
            -shiftLabels.xLabel.HTMLObject.position.x,
          );
          shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
          shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            // this.options.beam.shift.x / 2), - wierd behavior when shift is low
            shiftLabels.xLabel.HTMLObject.setDirection(
              shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
              ), 
              shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
              ),
              false
            );
          }
        }

        layer.addMesh(shiftLabels.xLabel.object);
        layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
      }
      if (shiftLabels.yLabel) {
        shiftLabels.yLabel.object.rotation.y = Math.PI / 2;
        shiftLabels.yLabel.object.position.x += this.middlePanel.size.depth / 2;
        if (options && options.is3DMode) {
          shiftLabels.yLabel.HTMLObject.position.set(
            shiftLabels.yLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
            -shiftLabels.yLabel.HTMLObject.position.z,
            -shiftLabels.yLabel.HTMLObject.position.x,
          );
          shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
          shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            // this.options.beam.shift.x / 2), - wierd behavior when shift is low
            shiftLabels.yLabel.HTMLObject.align = HTMLLabel.types.align.CENTER;
            shiftLabels.yLabel.HTMLObject.position.z += this.options.beam.shift.x < 0 ? -20 : 20; //padding
            shiftLabels.yLabel.HTMLObject.setDirection(
              shiftLabels.yLabel.HTMLObject.position.clone().setY(
                shiftLabels.yLabel.HTMLObject.position.y + Math.max(25, this.options.beam.shift.y / 2)
              ), 
              shiftLabels.yLabel.HTMLObject.position.clone().setY(
                shiftLabels.yLabel.HTMLObject.position.y - Math.max(25, this.options.beam.shift.y / 2)
              ),
              false
            );
          }
        }
        layer.addMesh(shiftLabels.yLabel.object);
        layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/edge-slab.js